import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../../services/auth.service';
import { AuthLead, AuthShell, AuthTitle, BackToLogin, DangerCallout, Field, FootNote, GhostButton, MintCallout, PasswordRules, PrimaryButton, TextLink, passwordRules } from './AuthShell';

/**
 * Sets the new password from the emailed link. Not a screen in the handoff
 * design, so it reuses the same dialog card, fields and "PASSWORD NEEDS"
 * checklist as sign-up, plus a confirm field (a typo here locks the user out).
 */
export function ResetPasswordView() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});
    const [serverError, setServerError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const rules = useMemo(() => passwordRules(password), [password]);
    const goLogin = () => navigate('/login');

    useEffect(() => {
        if (!success) return;
        const t = setTimeout(goLogin, 3000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;
        const next: typeof errors = {};
        if (!password) next.password = 'Choose a password.';
        else if (!rules.every((r) => r.ok)) next.password = 'Your password still misses the requirements below.';
        if (confirm !== password) next.confirm = 'Passwords do not match.';
        setErrors(next);
        if (Object.keys(next).length) return;

        setIsLoading(true);
        setServerError(null);
        try {
            await authService.resetPassword(token, password);
            setSuccess(true);
        } catch (err: any) {
            setServerError(err.response?.data?.message || 'Failed to reset password. The link may have expired.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!token) {
        return (
            <AuthShell>
                <BackToLogin onClick={goLogin} />
                <AuthTitle lead="This link has" accent="expired" marginTop={10} />
                <AuthLead>This password reset link is invalid or has already been used. Request a fresh one and try again.</AuthLead>
                <PrimaryButton type="button" onClick={() => navigate('/forgot-password')} marginTop={24}>Request a new link</PrimaryButton>
                <FootNote marginTop={20}>
                    Remembered it? <TextLink to="/login" onClick={goLogin}>Log in</TextLink>
                </FootNote>
            </AuthShell>
        );
    }

    if (success) {
        return (
            <AuthShell>
                <BackToLogin onClick={goLogin} />
                <AuthTitle lead="Password" accent="updated" marginTop={10} />
                <MintCallout icon="check">Your new password is saved. Taking you to log in…</MintCallout>
                <GhostButton onClick={goLogin}>Log in now</GhostButton>
            </AuthShell>
        );
    }

    return (
        <AuthShell>
            <BackToLogin onClick={goLogin} />
            <AuthTitle lead="Set a new" accent="password" marginTop={10} />
            <AuthLead>Choose something you don't use anywhere else.</AuthLead>

            <form onSubmit={handleSubmit} noValidate>
                <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Field label="New password" type="password" name="password" autoComplete="new-password" autoFocus value={password} onChange={(v) => { setPassword(v); setErrors((p) => ({ ...p, password: undefined })); }} placeholder="••••••••" error={errors.password} />
                    <Field label="Confirm password" type="password" name="confirmPassword" autoComplete="new-password" value={confirm} onChange={(v) => { setConfirm(v); setErrors((p) => ({ ...p, confirm: undefined })); }} placeholder="••••••••" error={errors.confirm} />
                </div>

                <PasswordRules rules={rules} hasError={!!errors.password} />

                {serverError && <DangerCallout>{serverError}</DangerCallout>}

                <PrimaryButton disabled={isLoading}>{isLoading ? 'Saving…' : 'Save new password'}</PrimaryButton>
            </form>

            <FootNote marginTop={20}>
                Remembered it? <TextLink to="/login" onClick={goLogin}>Log in</TextLink>
            </FootNote>
        </AuthShell>
    );
}
